import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dumbbell, Target, Flag, Plus, X, Trash2, TrendingUp } from "lucide-react";

const TIPOS = [
  ["tecnica", "Técnica"], ["remate", "Remate"], ["pierna_no_dominante", "Pierna no dominante"],
  ["penaltis", "Penaltis"], ["faltas", "Faltas"], ["velocidad", "Velocidad"], ["uno_vs_uno", "1 vs 1"],
  ["pase", "Pase"], ["control", "Control"], ["tactica", "Táctica"], ["otro", "Otro"],
];
const today = () => new Date().toISOString().slice(0, 10);

function Modal({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center" data-testid={testId}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border w-full max-w-md p-5 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-black uppercase">{title}</h3>
          <button onClick={onClose} data-testid={`${testId}-close`} className="w-8 h-8 border border-border hover:border-primary flex items-center justify-center"><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function IndividualModal({ playerId, onClose, onSaved }) {
  const [f, setF] = useState({ date: today(), tipo: "tecnica", duracion: "", repeticiones: "", aciertos: "", notas: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/familias/individual", {
        player_id: playerId, date: f.date, tipo: f.tipo,
        duracion_min: f.duracion ? Number(f.duracion) : null,
        repeticiones: f.repeticiones ? Number(f.repeticiones) : null,
        aciertos: f.aciertos !== "" ? Number(f.aciertos) : null,
        notas: f.notas.trim() || null,
      });
      toast.success("Entrenamiento individual guardado 💪");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Entreno individual" onClose={onClose} testId="familia-individual-modal">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <input type="date" className="input-dark" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="individual-date" />
        <select className="input-dark" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} data-testid="individual-tipo">
          {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <label className="space-y-1"><span className="text-muted-foreground">Duración (min)</span>
          <input type="number" min="1" className="input-dark w-full" placeholder="—" value={f.duracion} onChange={(e) => setF({ ...f, duracion: e.target.value })} data-testid="individual-duracion" /></label>
        <label className="space-y-1"><span className="text-muted-foreground">Intentos</span>
          <input type="number" min="1" className="input-dark w-full" placeholder="ej. 10" value={f.repeticiones} onChange={(e) => setF({ ...f, repeticiones: e.target.value })} data-testid="individual-reps" /></label>
        <label className="space-y-1"><span className="text-muted-foreground">Aciertos</span>
          <input type="number" min="0" className="input-dark w-full" placeholder="ej. 8" value={f.aciertos} onChange={(e) => setF({ ...f, aciertos: e.target.value })} data-testid="individual-aciertos" /></label>
      </div>
      {f.repeticiones && f.aciertos !== "" && Number(f.repeticiones) > 0 && (
        <div className="text-center font-heading font-black text-2xl text-primary" data-testid="individual-pct">
          {Math.round((100 * Number(f.aciertos)) / Number(f.repeticiones))}%
        </div>
      )}
      <input className="input-dark w-full" placeholder="Notas (opcional)" maxLength={400} value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} data-testid="individual-notas" />
      <button onClick={save} disabled={saving} className="btn-volt w-full h-11" data-testid="individual-save-btn">{saving ? "Guardando…" : "Guardar"}</button>
    </Modal>
  );
}

function TestModal({ playerId, catalog, onClose, onSaved }) {
  const keys = Object.keys(catalog);
  const [testKey, setTestKey] = useState(keys[0]);
  const [date, setDate] = useState(today());
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const items = catalog[testKey]?.items || [];
  const save = async () => {
    const results = {};
    for (const it of items) {
      const v = values[it.key];
      if (v !== undefined && v !== "") results[it.key] = Number(v);
    }
    if (!Object.keys(results).length) return toast.error("Introduce al menos un resultado");
    setSaving(true);
    try {
      await api.post("/familias/tests", { player_id: playerId, date, test_key: testKey, results });
      toast.success("Test guardado 🎯");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Registrar test" onClose={onClose} testId="familia-test-modal">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <select className="input-dark" value={testKey} onChange={(e) => { setTestKey(e.target.value); setValues({}); }} data-testid="test-key-select">
          {keys.map((k) => <option key={k} value={k}>{catalog[k].label}</option>)}
        </select>
        <input type="date" className="input-dark" value={date} onChange={(e) => setDate(e.target.value)} data-testid="test-date" />
      </div>
      <div className="space-y-2 text-xs">
        {items.map((it) => (
          <label key={it.key} className="flex items-center justify-between gap-3">
            <span className="text-muted-foreground">{it.label}{it.max ? ` (de ${it.max})` : ""}</span>
            <input type="number" min="0" max={it.max ?? undefined} className="input-dark w-24 text-center" placeholder="—"
              value={values[it.key] ?? ""} onChange={(e) => setValues({ ...values, [it.key]: e.target.value })} data-testid={`test-item-${it.key}`} />
          </label>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-volt w-full h-11" data-testid="test-save-btn">{saving ? "Guardando…" : "Guardar test"}</button>
    </Modal>
  );
}

function ChallengeModal({ playerId, onClose, onSaved }) {
  const [f, setF] = useState({ titulo: "", objetivo: "", unidad: "repeticiones", fecha_fin: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (f.titulo.trim().length < 3) return toast.error("Ponle un título al reto");
    if (!f.objetivo || Number(f.objetivo) < 1) return toast.error("Indica el objetivo total");
    setSaving(true);
    try {
      await api.post("/familias/retos", {
        player_id: playerId, titulo: f.titulo.trim(), objetivo_total: Number(f.objetivo),
        unidad: f.unidad.trim() || "repeticiones", fecha_fin: f.fecha_fin || null,
      });
      toast.success("¡Reto creado! 🚩");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Nuevo reto" onClose={onClose} testId="familia-reto-modal">
      <input className="input-dark w-full" placeholder="Ej. 200 pases con la izquierda" maxLength={120} value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })} data-testid="reto-titulo" />
      <div className="grid grid-cols-3 gap-2 text-xs">
        <label className="space-y-1"><span className="text-muted-foreground">Objetivo total</span>
          <input type="number" min="1" className="input-dark w-full" placeholder="200" value={f.objetivo} onChange={(e) => setF({ ...f, objetivo: e.target.value })} data-testid="reto-objetivo" /></label>
        <label className="space-y-1"><span className="text-muted-foreground">Unidad</span>
          <input className="input-dark w-full" value={f.unidad} onChange={(e) => setF({ ...f, unidad: e.target.value })} data-testid="reto-unidad" /></label>
        <label className="space-y-1"><span className="text-muted-foreground">Fecha límite</span>
          <input type="date" className="input-dark w-full" value={f.fecha_fin} onChange={(e) => setF({ ...f, fecha_fin: e.target.value })} data-testid="reto-fecha" /></label>
      </div>
      <button onClick={save} disabled={saving} className="btn-volt w-full h-11" data-testid="reto-save-btn">{saving ? "Creando…" : "Crear reto"}</button>
    </Modal>
  );
}

export const ImproveSection = ({ players }) => {
  const [playerId, setPlayerId] = useState(players[0]?.id);
  const [retos, setRetos] = useState([]);
  const [tests, setTests] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [modal, setModal] = useState(null);
  const [openHistory, setOpenHistory] = useState(null);

  const load = useCallback(async () => {
    if (!playerId) return;
    try {
      const [r, t, s, c] = await Promise.all([
        api.get(`/familias/retos?player_id=${playerId}`),
        api.get(`/familias/tests?player_id=${playerId}`),
        api.get(`/familias/individual?player_id=${playerId}`),
        api.get("/familias/tests/catalog"),
      ]);
      setRetos(r.data); setTests(t.data); setSessions(s.data); setCatalog(c.data);
    } catch { /* silent */ }
  }, [playerId]);
  useEffect(() => { load(); }, [load]);

  const addProgress = async (reto) => {
    const v = window.prompt(`¿Cuántas ${reto.unidad} sumas hoy a "${reto.titulo}"?`, "50");
    if (!v) return;
    const n = Number(v);
    if (!n || n < 1) return toast.error("Cantidad no válida");
    try {
      const { data } = await api.post(`/familias/retos/${reto.id}/progress`, { cantidad: n });
      if (data.completado) toast.success("🏆 ¡OBJETIVO CONSEGUIDO! Enhorabuena");
      else toast.success(`+${n} · vas ${data.progreso}/${data.objetivo_total}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo registrar"); }
  };

  const del = async (kind, id) => {
    if (!window.confirm("¿Borrar?")) return;
    try { await api.delete(`/familias/${kind}/${id}`); load(); }
    catch { toast.error("No se pudo borrar"); }
  };

  // Último test de cada tipo con delta vs anterior
  const latestByKey = {};
  for (const t of tests) latestByKey[t.test_key] = [...(latestByKey[t.test_key] || []), t];

  if (!players.length) return null;
  return (
    <div className="space-y-3" data-testid="familia-improve-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading font-black uppercase text-sm text-muted-foreground">Mejora individual</h2>
        {players.length > 1 && (
          <select className="input-dark h-8 text-xs" value={playerId} onChange={(e) => setPlayerId(e.target.value)} data-testid="improve-player-select">
            {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <button onClick={() => setModal("individual")} data-testid="improve-new-individual-btn" className="h-10 border border-border hover:border-primary text-[11px] uppercase font-heading tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors"><Dumbbell size={13} className="text-primary" /> + Entreno individual</button>
        <button onClick={() => setModal("test")} data-testid="improve-new-test-btn" className="h-10 border border-border hover:border-primary text-[11px] uppercase font-heading tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors"><Target size={13} className="text-primary" /> + Test</button>
        <button onClick={() => setModal("reto")} data-testid="improve-new-reto-btn" className="h-10 border border-border hover:border-primary text-[11px] uppercase font-heading tracking-wider inline-flex items-center justify-center gap-1.5 transition-colors"><Flag size={13} className="text-primary" /> + Reto</button>
      </div>

      {retos.length > 0 && (
        <div className="space-y-2" data-testid="improve-retos-list">
          {retos.map((r) => {
            const pct = Math.min(100, Math.round((100 * r.progreso) / r.objetivo_total));
            return (
              <div key={r.id} className={`border p-3 space-y-2 ${r.completado ? "border-emerald-500/60" : "border-border"}`} data-testid={`improve-reto-${r.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-heading font-bold text-xs uppercase truncate">{r.completado ? "🏆 " : ""}{r.titulo}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!r.completado && (
                      <button onClick={() => addProgress(r)} data-testid={`improve-reto-add-${r.id}`} className="h-7 px-2.5 border border-primary/60 text-primary hover:bg-primary/10 text-[10px] uppercase font-heading tracking-wider inline-flex items-center gap-1"><Plus size={10} /> Sumar</button>
                    )}
                    <button onClick={() => del("retos", r.id)} data-testid={`improve-reto-del-${r.id}`} className="w-7 h-7 border border-border hover:border-red-500 hover:text-red-400 flex items-center justify-center"><Trash2 size={11} /></button>
                  </div>
                </div>
                <div className="h-2.5 bg-border/40">
                  <div className={`h-full ${r.completado ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[10px] text-muted-foreground font-mono">{r.progreso} / {r.objetivo_total} {r.unidad}{r.fecha_fin ? ` · hasta ${r.fecha_fin}` : ""}</div>
              </div>
            );
          })}
        </div>
      )}

      {Object.keys(latestByKey).length > 0 && (
        <div className="border border-border" data-testid="improve-tests-list">
          <div className="px-3 py-2 border-b border-border flex items-center gap-1.5"><TrendingUp size={12} className="text-primary" /><span className="overline text-[10px]">Tests · evolución</span></div>
          <div className="divide-y divide-border">
            {Object.entries(latestByKey).map(([key, arr]) => {
              const last = arr[arr.length - 1];
              const prev = arr.length > 1 ? arr[arr.length - 2] : null;
              const items = catalog[key]?.items || [];
              const isOpen = openHistory === key;
              return (
                <div key={key} className="p-3 text-xs" data-testid={`improve-test-${key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="font-heading font-bold uppercase">{catalog[key]?.label || key} <span className="text-muted-foreground font-normal normal-case">· {last.date}</span></div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        {items.map((it) => {
                          const v = last.results[it.key];
                          if (v === undefined) return null;
                          const pv = prev?.results?.[it.key];
                          const delta = pv !== undefined ? v - pv : null;
                          return (
                            <span key={it.key} className="font-mono">
                              {it.label}: <b>{v}{it.max ? `/${it.max}` : ""}</b>
                              {delta !== null && delta !== 0 && <span className={delta > 0 ? "text-emerald-400" : "text-red-400"}> {delta > 0 ? "▲" : "▼"}{Math.abs(delta)}</span>}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <button onClick={() => del("tests", last.id)} data-testid={`improve-test-del-${last.id}`} className="w-7 h-7 border border-border hover:border-red-500 hover:text-red-400 flex items-center justify-center shrink-0"><Trash2 size={11} /></button>
                  </div>
                  {arr.length > 1 && (
                    <button onClick={() => setOpenHistory(isOpen ? null : key)} data-testid={`improve-test-history-btn-${key}`} className="mt-2 text-[10px] uppercase font-heading tracking-wider text-muted-foreground hover:text-primary">
                      {isOpen ? "− Ocultar historial" : `+ Historial (${arr.length})`}
                    </button>
                  )}
                  {isOpen && (
                    <div className="mt-2 space-y-1.5 border-l-2 border-border pl-3" data-testid={`improve-test-history-${key}`}>
                      {[...arr].reverse().map((t) => (
                        <div key={t.id} className="flex items-center justify-between gap-2 font-mono text-[10px] text-muted-foreground">
                          <span>{t.date} · {items.map((it) => t.results[it.key] !== undefined ? `${it.label}: ${t.results[it.key]}${it.max ? `/${it.max}` : ""}` : null).filter(Boolean).join(" · ")}</span>
                          <button onClick={() => del("tests", t.id)} data-testid={`improve-test-del-${t.id}`} className="w-6 h-6 border border-border hover:border-red-500 hover:text-red-400 flex items-center justify-center shrink-0"><Trash2 size={9} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sessions.length > 0 && (
        <div className="border border-border divide-y divide-border" data-testid="improve-individual-list">
          {sessions.slice(0, 5).map((s) => (
            <div key={s.id} className="p-3 text-xs flex items-center gap-2" data-testid={`improve-individual-${s.id}`}>
              <Dumbbell size={12} className="text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <b className="uppercase font-heading">{TIPOS.find(([v]) => v === s.tipo)?.[1] || s.tipo}</b>
                <span className="text-muted-foreground"> · {s.date}{s.duracion_min ? ` · ${s.duracion_min}'` : ""}{s.pct !== null && s.pct !== undefined ? ` · ${s.aciertos}/${s.repeticiones} (${s.pct}%)` : ""}{s.notas ? ` · ${s.notas}` : ""}</span>
              </div>
              <button onClick={() => del("individual", s.id)} data-testid={`improve-individual-del-${s.id}`} className="w-7 h-7 border border-border hover:border-red-500 hover:text-red-400 flex items-center justify-center shrink-0"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {modal === "individual" && <IndividualModal playerId={playerId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === "test" && <TestModal playerId={playerId} catalog={catalog} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === "reto" && <ChallengeModal playerId={playerId} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
};
